import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import logger from './utils/logger';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './components/Login';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import NotificationPopup from './components/NotificationPopup';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import PageLoader from './components/PageLoader';
import { Toaster } from 'react-hot-toast';
import { registerServiceWorker } from './services/pushNotifications';
import { initializeTenantConfig } from './config/tenant.config';
import { TourProvider } from './contexts/TourContext';
import GuidedTour from './components/GuidedTour';

// Lazy-loaded page components for code splitting
const Dashboard = React.lazy(() => import('./pages/EnhancedDashboard'));
const SensorDetail = React.lazy(() => import('./pages/EnhancedSensorDetail'));
const ManageRooms = React.lazy(() => import('./pages/ManageRooms'));
const ManageSensors = React.lazy(() => import('./pages/ManageSensors'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Logbook = React.lazy(() => import('./pages/Logbook'));
const TenantManagement = React.lazy(() => import('./pages/TenantManagement'));
const UserManagement = React.lazy(() => import('./pages/UserManagement'));
const DeviceManagement = React.lazy(() => import('./pages/DeviceManagement'));
const AlertsPage = React.lazy(() => import('./pages/AlertsPage'));
const AuditReports = React.lazy(() => import('./pages/AuditReports'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const SchedulerPage = React.lazy(() => import('./pages/SchedulerPage'));
const AIPredictionsPage = React.lazy(() => import('./pages/AIPredictionsPage'));
const HubManagement = React.lazy(() => import('./pages/HubManagement'));

// Initialize tenant configuration on app load
initializeTenantConfig();

function App() {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  // Register service worker and listen for updates
  useEffect(() => {
    // Delay service worker registration to not block initial render
    const timer = setTimeout(() => {
      registerServiceWorker().catch(err => {
        logger.warn('Service worker registration skipped:', err);
      });
    }, 3000);

    // Listen for service worker update messages
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SW_UPDATED') {
        // On the login page nothing can be lost, so apply the update silently
        // (once per session, to rule out a reload loop) instead of greeting
        // the user with a "new version" banner before they have done anything.
        const onLogin = window.location.pathname === '/login';
        let alreadyReloaded = false;
        try { alreadyReloaded = sessionStorage.getItem('sw_auto_reloaded') === '1'; } catch { /* storage blocked */ }
        if (onLogin && !alreadyReloaded) {
          try { sessionStorage.setItem('sw_auto_reloaded', '1'); } catch { /* storage blocked */ }
          window.location.reload();
          return;
        }
        setShowUpdateBanner(true);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    // Check for updates periodically (every 5 minutes)
    const updateCheck = setInterval(() => {
      navigator.serviceWorker?.ready.then(registration => {
        registration.update();
      });
    }, 5 * 60 * 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(updateCheck);
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <ErrorBoundary>
    <TenantProvider>
      <AuthProvider>
        <Router>
        <TourProvider>
          <GuidedTour />
          {/* App Update Banner */}
          {showUpdateBanner && (
            <div className="fixed top-0 left-0 right-0 bg-blue-600 text-white px-4 py-3 z-50 flex items-center justify-between shadow-lg">
              <span className="text-sm">A new version is available!</span>
              <button
                onClick={handleRefresh}
                className="bg-white text-blue-600 px-4 py-1 rounded text-sm font-semibold hover:bg-blue-50"
              >
                Refresh Now
              </button>
            </div>
          )}
          {/* Offline Detection Banner */}
          <OfflineBanner />
          {/* PWA Install Banner */}
          <PWAInstallPrompt />
          {/* Global Notification Popup */}
          <NotificationPopup />
          {/* Toast Notifications */}
          <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
          <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/sensor/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <SensorDetail />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/rooms"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout>
                  <ManageRooms />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/sensors"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout>
                  <ManageSensors />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/logbook"
            element={
              <ProtectedRoute>
                <Layout>
                  <Logbook />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tenants"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout>
                  <TenantManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'manager']}>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/devices"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout>
                  <DeviceManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/hubs"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout>
                  <HubManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Phase 8: Alerts Page (all authenticated users) */}
          <Route
            path="/alerts"
            element={
              <ProtectedRoute>
                <Layout>
                  <AlertsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Phase 8: Analytics Dashboard (supervisor+) */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'manager', 'supervisor']}>
                <Layout>
                  <Analytics />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Phase 8: Audit & Reports (admin + manager+) */}
          <Route
            path="/audit"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'manager']}>
                <Layout>
                  <AuditReports />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Phase 8: Coming Soon - Scheduler (admin+) */}
          <Route
            path="/scheduler"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <Layout>
                  <SchedulerPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* E-Signatures moved into Audit & Reports tab */}

          {/* Phase 8: Coming Soon - AI Predictions (admin+) */}
          <Route
            path="/predictions"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <Layout>
                  <AIPredictionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Catch all - 404 page */}
          <Route path="*" element={<Layout><NotFound /></Layout>} />
        </Routes>
          </Suspense>
        </TourProvider>
        </Router>
      </AuthProvider>
    </TenantProvider>
    </ErrorBoundary>
  );
}

export default App;
